import React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { BoltIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { utcDateLabel } from "../../../utils/utcTime";
import ButtonLoader from "../../../components/ButtonLoader";
import { getRfiRequestLocationDisplay } from "../../../utils/myRfiGridRowMapper";
import { formatVqqName } from "../../../utils/vqqNameFormat";

// Blur-backdrop confirmation per ticket. Title + body + 3 acknowledgement bullets +
// side-by-side Cancel/Confirm buttons matching platform style.
const AutoreleaseConfirmModal = ({
  open,
  onClose,
  eligibility,
  selectedPast,
  rfisDetails,
  onConfirm,
  loading = false,
}) => {
  if (!selectedPast) return null;

  const templateName =
    eligibility?.template_name || formatVqqName(rfisDetails?.name) || "Diligent";
  const pastSponsor = selectedPast.sponsor_name || "the previous sponsor";
  const pastDate = utcDateLabel(selectedPast.release_date, "MMMM D, YYYY", "the submission date");
  const locationLabel =
    getRfiRequestLocationDisplay(selectedPast) || "matching";
  const currentSponsor =
    eligibility?.current_sponsor_name || rfisDetails?.sponsorName || "this sponsor";

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 backdrop-blur-sm bg-transparent transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl ring-1 ring-gray-200 transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
          >
            <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-indigo-100">
              <BoltIcon className="size-6 text-indigo-600" aria-hidden="true" />
            </div>

            <div className="mt-3 text-center sm:mt-5">
              <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                Autorelease VQQ
              </DialogTitle>
              <div className="mt-3">
                <p className="text-sm text-gray-700 leading-relaxed">
                  By clicking <span className="font-semibold">Confirm</span>, you will
                  autorelease your completed{" "}
                  <span className="font-semibold">{templateName}</span> Vendor
                  Qualification Questionnaire (VQQ) populated with the responses you
                  last approved and submitted to{" "}
                  <span className="font-semibold">{pastSponsor}</span> on{" "}
                  <span className="font-semibold">{pastDate}</span> for your{" "}
                  <span className="font-semibold">{locationLabel}</span> segment.
                </p>
              </div>
            </div>

            <ul className="mt-5 space-y-2 text-left text-sm text-gray-700">
              <li className="flex gap-2">
                <CheckCircleIcon
                  className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>
                  I acknowledge that I have reviewed the responses being autoreleased and
                  confirm they remain accurate for the request from{" "}
                  <span className="font-semibold">{currentSponsor}</span>.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon
                  className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>
                  I understand Autorelease is only offered when responses are less than
                  1 year old and the Diligent VQQ template has not been updated since
                  they were last submitted.
                </span>
              </li>
              <li className="flex gap-2">
                <CheckCircleIcon
                  className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>
                  Status will move to <span className="font-semibold">Complete</span>{" "}
                  and the VQQ will be sent to{" "}
                  <span className="font-semibold">{currentSponsor}</span>.
                </span>
              </li>
            </ul>

            <div className="mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
              <button
                type="button"
                onClick={onConfirm}
                disabled={loading}
                className={`inline-flex w-full justify-center items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:col-start-2 transition ${
                  loading
                    ? "bg-indigo-400 cursor-not-allowed"
                    : "bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                }`}
              >
                {loading ? (
                  <>
                    <ButtonLoader />
                    <span>Autoreleasing…</span>
                  </>
                ) : (
                  "Confirm"
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                data-autofocus
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default AutoreleaseConfirmModal;
